import React, {useEffect, useState, useCallback, useMemo} from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";


import VyberKvetinForm from "./components/VyberKvetinForm";
import RecForm from "./components/RecForm";
import { getToken, logout as authLogout } from "./auth/authClient";
import LoginPage from "./components/LoginPage";
import StartPage from "./components/StartPage";
import ZakladPage from "./components/ZakladPage";
import VyjezdyPage from "./components/VyjezdyPage";
import VstupniInformaceForm from "./components/VstupniInformaceForm";
import ObradForm from "./components/ObradForm";
import PrevozForm from "./components/PrevozForm";
import SeznamSluzebForm from "./components/SeznamSluzebForm";
import VyberSluzebFormV2 from "./components/VyberSluzebFormV2";
import VypravitelForm from "./components/VypravitelForm";
import ZesnulaOsobaForm from "./components/ZesnulaOsobaForm";
import DokumentyForm from "./components/DokumentyForm";
import FinalniObjednavka from "./components/FinalniObjednavka";
import KnihaZemrelych from "./components/KnihaZemrelych";
import FotoDokumentace from "./components/FotoDokumentace";
import Statistiky from "./components/Statistiky";
import KalendarObrady from "./components/KalendarObrady";
import UdrzbaPage from "./components/UdrzbaPage";
import ModuleSelectPage from "./components/ModuleSelectPage";
import Pohrebka2App from "./pohrebka2/pages/Pohrebka2App";
import UdrzbaVerejnyForm from "./components/udrzba/UdrzbaVerejnyForm";
import Pohrebka2RecPublicPage from "./pohrebka2/pages/Pohrebka2RecPublicPage";
import { useFormData } from "./context/FormDataContext";

const LS_MODE = "pohrebka_appMode";
const LS_GROUP = "pohrebka_openGroup";

export default function App() {
  const {
    knihyApiNacist,
    resetForm,
    createOrder,
    setCurrentOrderId,
    setIsEditingExisting,
    currentOrderId,
    saveOrder,
  } = useFormData();

  const [activeTab, setActiveTab] = useState("start");
  const isPublicUdrzbaRoute =
    typeof window !== "undefined" &&
    (
      window.location.hostname === "udrzbahrobu.pspohrebka.cz" ||
      window.location.pathname.startsWith("/udrzba") ||
      window.location.pathname.startsWith("/udrzba/")
    );

  const isPublicRecRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/rec-rodina/");

  const [appMode, setAppMode] = useState(() =>
      isPublicUdrzbaRoute ? "udrzba_form" : (isPublicRecRoute ? "rec_public" : null)
  );

    const effectiveOrderId = String(currentOrderId || "").trim();

  const orderKey = String(effectiveOrderId || "new");

  if (isPublicUdrzbaRoute) return <UdrzbaVerejnyForm />;
  if (isPublicRecRoute) return <Pohrebka2RecPublicPage />;
  // === WORKFLOW (zakázka) ===
  // ukládá: (a) které kroky jsou hotové, (b) poslední otevřená zakázková záložka
  const WF_KEY = useCallback((orderId) => {
    const id = String(orderId || "").trim() || "new";
    return `pohrebka_wf_${id}`;
  }, []);

  const wfLoad = useCallback((orderId) => {
    try {
      const raw = localStorage.getItem(WF_KEY(orderId));
      return raw ? JSON.parse(raw) : { done: {}, lastTab: "zaklad" };
    } catch (e) {
      return { done: {}, lastTab: "zaklad" };
    }
  }, [WF_KEY]);

  const wfSave = useCallback((orderId, wf) => {
    try {
      localStorage.setItem(
        WF_KEY(orderId),
        JSON.stringify(wf || { done: {}, lastTab: "zaklad" })
      );
    } catch (e) {}
  }, [WF_KEY]);

  const zakazkaFlow = useMemo(() => ([
    "zaklad",
    "vstupni",
    "vypravitel",
    "zesnula",
    "obrad",
    "prevoz",
    "sluzby",
    "kvetiny",
    "rec",
    "dokumenty",
    "final",
  ]), []);

  const zakazkaLabels = useMemo(() => ({
    zaklad: "Základ",
    vstupni: "Vstupní informace",
    vypravitel: "Vypravitel",
    zesnula: "Zesnulá osoba",
    obrad: "Obřad",
    prevoz: "Převoz",
    sluzby: "Výběr služeb",
    kvetiny: "Květiny",
    rec: "Řeč",
    dokumenty: "Dokumenty",
    final: "Finální objednávka",
  }), []);

  const zakazkaNext = useCallback((tab) => {
    const i = zakazkaFlow.indexOf(tab);
    return i >= 0 ? (zakazkaFlow[i + 1] || "final") : "final";
  }, [zakazkaFlow]);

  const zakazkaPrev = useCallback((tab) => {
    const i = zakazkaFlow.indexOf(tab);
    return i > 0 ? zakazkaFlow[i - 1] : "zaklad";
  }, [zakazkaFlow]);

  const isZakazkaTab = useCallback((t) => new Set(zakazkaFlow).has(t), [zakazkaFlow]);

  const canSeeZakazkaTab = useCallback((tab) => {
    if (tab === "zaklad") return true;
    if (tab === "vstupni") return true;

    const wf = wfLoad(effectiveOrderId);
    const done = wf && wf.done ? wf.done : {};
    const prev = zakazkaPrev(tab);
    return Boolean(done[prev]);
  }, [wfLoad, effectiveOrderId, zakazkaPrev]);
  // === AUTH gate (token je jediná pravda) ===
  const [isAuthed, setIsAuthed] = useState(() => ((isPublicUdrzbaRoute || isPublicRecRoute) ? true : Boolean(getToken())));

  const handleLoggedIn = (_user = null, moduleChoice = null) => {
    setIsAuthed(true);
    const picked =
      String(
        moduleChoice ||
        (typeof localStorage !== "undefined" ? (localStorage.getItem("pohrebka_appMode") || "") : "") ||
        ""
      ).trim();

    if (picked === "udrzba") {
      setAppMode("udrzba");
      setOpenGroup(null);
      setActiveTab("start");
      return;
    }

    setAppMode("pohrebka");
    setOpenGroup(null);
    setActiveTab("start");
  };

  const handleLogout = () => {
    try { authLogout(); } catch (e) {}
    setIsAuthed(false);
  };
// null | "zakazka" | "vyjezdy" | "kalendar" | "hledat"
  const [openGroup, setOpenGroup] = useState(null); // "zakazka" | "evidence" | "planovani" | "prehledy"

  // restore mode/group
  useEffect(() => {
    if (isPublicUdrzbaRoute) {
      setAppMode("udrzba_form");
      setOpenGroup(null);
      setActiveTab("start");
      return;

      if (isPublicRecRoute) {
        setAppMode("rec_public");
        setOpenGroup(null);
        setActiveTab("start");
        return;
      }
    }

    try {
      const m = localStorage.getItem(LS_MODE);
      const g = localStorage.getItem(LS_GROUP);
      if (m) setAppMode(m);
      if (g) setOpenGroup(g);
    } catch {}
  }, [isPublicUdrzbaRoute, isPublicRecRoute]);

  useEffect(() => {
    try {
      if (appMode) localStorage.setItem(LS_MODE, appMode);
      else localStorage.removeItem(LS_MODE);
    } catch {}
  }, [appMode]);

  useEffect(() => {
    try {
      if (openGroup) localStorage.setItem(LS_GROUP, openGroup);
      else localStorage.removeItem(LS_GROUP);
    } catch {}
  }, [openGroup]);

  const handleOpenZakazka = useCallback(
      async (id) => {
        const zid = String(id || "").trim();
        if (!zid) return;

        // ✅ nastav aktivní zakázku HNED (jinak context hlásí orderId: "")
        try { if (typeof setIsEditingExisting === "function") setIsEditingExisting(true); } catch (e) {}          // 🔒 OPEN GUARD: nejdřív načti zakázku, až pak přepni currentOrderId
          let openedOk = false;
          try {
                      // ✅ FIX: přepni currentOrderId HNED (jinak auto-loady vezmou staré ID a přepíšou formData zpět)
// (removed) preload setCurrentOrderId – handled inside openZakazkaById
          const r = await knihyApiNacist(zid);
            openedOk =
              (r === true) ||
              (r && typeof r === "object" && (r.ok === true || r.success === true)) ||
              (r && typeof r === "object" && (r.error == null));
          } catch (e) {
            openedOk = false;
          }

          if (!openedOk) {
            // load selhal (typicky 401/403) -> NEPŘEPÍNEJ currentOrderId
            return;
          }
          // currentOrderId už nastavuje openZakazkaById uvnitř FormDataContext
        // 2) přepnout UI do Zakázka
        setAppMode("zakazka");
        setOpenGroup("zakazka");
        setActiveTab("zaklad");

        // 3) uložit poslední otevřenou
        try {
          localStorage.setItem("lastOpenedOrderId", zid);
        } catch {}
      },
      [knihyApiNacist, setCurrentOrderId, setIsEditingExisting, setAppMode, setOpenGroup, wfLoad]
    );

  // když uživatel přepne group, nastav tab defaultně na něco platného
  useEffect(() => {
    if (!openGroup) return;
    const defaults = {
      zakazka: "zaklad",
      evidence: "kniha",
      planovani: "vyjezdy",
      prehledy: "stat",
    };
    const want = defaults[openGroup] || "zaklad";
    // pokud je aktivní tab něco mimo, tak to srovnej
    const allowed = {
      zakazka: new Set(["zaklad", "vstupni", "vypravitel", "zesnula", "prevoz", "obrad", "sluzby", "kvetiny", "rec", "dokumenty", "final"]),
      evidence: new Set(["kniha", "foto"]),
      planovani: new Set(["vyjezdy", "obrad1"]),
      prehledy: new Set(["stat"]),
    };
    if (!allowed[openGroup]?.has(activeTab)) setActiveTab(want);
  }, [openGroup, activeTab]);

  const handleContinue = useCallback(async () => {
    let zid = "";
    try { zid = localStorage.getItem("lastOpenedOrderId") || ""; } catch (e) {}
    zid = String(zid || "").trim();
    if (!zid) {
      alert("Není uložená žádná poslední zakázka (lastOpenedOrderId). Otevři zakázku z Knihy nebo založ novou.");
      return;
    }
    await handleOpenZakazka(zid);
  }, [handleOpenZakazka]);

  const handleNovaZakazka = useCallback(async () => {
    // ✅ tvrdý reset, aby se nic nelepil na staré ID (typicky 114)
    try { resetForm(); } catch (e) {}
    try { if (typeof setIsEditingExisting === "function") setIsEditingExisting(false); } catch (e) {}
    try { if (typeof setCurrentOrderId === "function") setCurrentOrderId(""); } catch (e) {}

    try {
      localStorage.removeItem("lastOpenedOrderId");
      localStorage.removeItem("currentOrderId");
    } catch (e) {}

    // založ novou zakázku (context si nastaví nové ID)
    try { await createOrder(); } catch (e) {}

    setAppMode("zakazka");
    setOpenGroup("zakazka");
    setActiveTab("zaklad");
  }, [resetForm, createOrder, setCurrentOrderId, setIsEditingExisting]);


  function ActionBar() {
    if (!isAuthed) {

      // ✅ pod loginem nesmí být nic dalšího

      
  /* BUILD_MARKER_VYJEZDY_MODAL1 */
  try { console.log('BUILD_MARKER_VYJEZDY_MODAL1'); } catch {}
return (

        <div className="min-h-screen bg-gray-50 p-4">

          <LoginPage onLoggedIn={handleLoggedIn} />

        </div>

      );

    }



    return (
      <div className="flex flex-wrap gap-2 p-3 border-b bg-white sticky top-0 z-20">
        <button className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50" onClick={() => { handleLogout(); }}>
          Odhlásit
        </button>

        <button
          className="px-3 py-2 rounded-lg border hover:bg-gray-50"
          onClick={() => {
            
             handleNovaZakazka();

          }}
        >
          ➕ Nová zakázka
        </button>

        <button
          className="px-3 py-2 rounded-lg border hover:bg-gray-50"
          onClick={async () => {
            // pokud existuje poslední ID, otevři ho. jinak jen přepni UI
            let last = "";
            try {
              last = localStorage.getItem("lastOpenedOrderId") || "";
            } catch {}
            if (String(last).trim()) await handleOpenZakazka(last);
            else {
              setAppMode("zakazka");
              setOpenGroup("zakazka");
              setActiveTab("zaklad");
            }
          }}
        >
          ▶️ Pokračovat (aktivní)
        </button>

        <button
          className="px-3 py-2 rounded-lg border hover:bg-gray-50"
          onClick={() => {
             // 🛡️ AUTO-CONTINUE GUARD: nespouštěj, pokud už máme otevřenou zakázku
            try {
              const hasId =
                (typeof currentOrderId !== "undefined" && String(currentOrderId || "").trim()) ||
                (typeof localStorage !== "undefined" && String(localStorage.getItem("currentOrderId") || "").trim());
              if (!hasId) {
                handleContinue();
              } else {
                // console.log("AUTO-CONTINUE skipped; currentOrderId=", hasId);
              }
            } catch (e) {
              try { handleContinue(); } catch (_e2) {}
            }
}}
        >
          🔎 Hledat zakázku
        </button>

        <button
          className="px-3 py-2 rounded-lg border hover:bg-gray-50"
          onClick={() => {
            setAppMode("vyjezdy");
            setOpenGroup("planovani");
            setActiveTab("vyjezdy");
          }}
        >
          🚑 Výjezd
        </button>

        <button
          className="px-3 py-2 rounded-lg border hover:bg-gray-50"
          onClick={() => {
            setAppMode("kalendar");
            setOpenGroup("planovani");
            setActiveTab("obrad1");
          }}
        >
          📅 Kalendář obřadů
        </button>

        <div className="flex-1" />

        <button
          className="px-3 py-2 rounded-lg border hover:bg-gray-50"
          onClick={() => {
            setAppMode(null);
            setOpenGroup(null);
            setActiveTab("start");
          }}
        >
          ⛔ Zpět na výběr modulu
        </button>
      </div>
    );
  }

  function GroupButton({ id, title }) {
    const isOpen = openGroup === id;

    const pickDefaultTab = (groupId) => {
      if (groupId === "zakazka") return "zaklad";
      if (groupId === "evidence") return "kniha";
      if (groupId === "planovani") return "vyjezdy";
      if (groupId === "prehledy") return "stat";
      return "zaklad";
    };

    return (
      <button
        className={`w-full text-left px-3 py-2 rounded-lg border ${isOpen ? "bg-gray-50" : "bg-white"} hover:bg-gray-50`}
        onClick={() => {
          const next = id; // FIX: group never closes on click (prevents flicker)
          setOpenGroup(next);
          if (next) setActiveTab(pickDefaultTab(next));
        }}
      >
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium text-gray-900">{title}</div>
            <div className="text-sm text-gray-400">{isOpen ? "▾" : "▸"}</div>
          </div>
          {!isOpen && (
            <div className="text-xs text-gray-500">Klikni pro zobrazení záložek</div>
          )}
      </button>
    );
  }

  const showTabs = appMode === "pohrebka" || appMode === "zakazka" || appMode === "vyjezdy" || appMode === "kalendar";


  // === LOGIN_ONLY_GATE_APP ===
  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoginPage onLoggedIn={handleLoggedIn} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showTabs ? <ActionBar /> : null}

      {appMode === "udrzba_form" ? (
        <UdrzbaVerejnyForm />
      ) : null}

      {appMode === "pohrebka2" ? (
        <Pohrebka2App />
      ) : appMode === "udrzba" ? (


        <UdrzbaPage
            onLogout={handleLogout}
            onBackToModules={() => {
              setAppMode(null);
              setOpenGroup(null);
              setActiveTab("start");
            }}
          />


      ) : !showTabs ? (


        <ModuleSelectPage
          onPickPohrebka2={() => setAppMode("pohrebka2")}


          onPickPohrebka={() => {


            setAppMode("pohrebka");


            setOpenGroup(null);


            setActiveTab("start");


          }}


          onPickUdrzba={() => {


            setAppMode("udrzba");


            setOpenGroup(null);


            setActiveTab("start");


          }}


          onLogout={handleLogout}


        />


      ) : (


        <div className="p-4">
          <div className="grid gap-2 mb-3">
            <GroupButton id="zakazka" title="A) Zakázka (jádro)" />
            <GroupButton id="evidence" title="B) Evidence (kniha, fotky)" />
            <GroupButton id="planovani" title="C) Plánování (výjezdy, kalendář)" />
            <GroupButton id="prehledy" title="D) Přehledy (statistiky)" />
          </div>

          <Tabs key={"tabs-" + String(effectiveOrderId || "")} value={activeTab} onValueChange={setActiveTab} className="bg-white rounded-xl border">
            <TabsList className="flex flex-wrap gap-2 p-2 border-b">
              {openGroup === "zakazka" && (
                <>
                                    <TabsTrigger value="zaklad">Základ</TabsTrigger>
                  <TabsTrigger value="vstupni">Vstupní informace</TabsTrigger>
                  {canSeeZakazkaTab("vypravitel") && <TabsTrigger value="vypravitel">Vypravitel</TabsTrigger>}
                  {canSeeZakazkaTab("zesnula") && <TabsTrigger value="zesnula">Zesnulá osoba</TabsTrigger>}
                  {canSeeZakazkaTab("obrad") && <TabsTrigger value="obrad">Obřad</TabsTrigger>}
                  {canSeeZakazkaTab("prevoz") && <TabsTrigger value="prevoz">Převoz</TabsTrigger>}
                  {canSeeZakazkaTab("sluzby") && <TabsTrigger value="sluzby">Výběr služeb</TabsTrigger>}
                  {canSeeZakazkaTab("kvetiny") && <TabsTrigger value="kvetiny">Květiny</TabsTrigger>}
                  {canSeeZakazkaTab("rec") && <TabsTrigger value="rec">Řeč</TabsTrigger>}
                  {canSeeZakazkaTab("dokumenty") && <TabsTrigger value="dokumenty">Dokumenty</TabsTrigger>}
                  {canSeeZakazkaTab("final") && <TabsTrigger value="final">Finální objednávka</TabsTrigger>}
                </>
              )}

              {openGroup === "evidence" && (
                <>
                  <TabsTrigger value="kniha">Kniha zemřelých</TabsTrigger>
                  <TabsTrigger value="foto">Foto dokumentace</TabsTrigger>
                </>
              )}

              {openGroup === "planovani" && (
                <>
                  <TabsTrigger value="vyjezdy">Výjezd</TabsTrigger>
                  <TabsTrigger value="obrad1">Kalendář obřadů</TabsTrigger>
                </>
              )}

              {openGroup === "prehledy" && (
                <>
                  <TabsTrigger value="stat">Statistiky</TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="zaklad" className="p-3">{activeTab === "zaklad" ? <ZakladPage onOpenZakazka={handleOpenZakazka} openingId="" goTab={setActiveTab} /> : null}</TabsContent>
            <TabsContent value="vstupni" className="p-3">{activeTab === "vstupni" ? <VstupniInformaceForm key={`vstupni:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="vypravitel" className="p-3">{activeTab === "vypravitel" ? <VypravitelForm key={`vypravitel:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="zesnula" className="p-3">{activeTab === "zesnula" ? <ZesnulaOsobaForm key={`zesnula:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="prevoz" className="p-3">{activeTab === "prevoz" ? <PrevozForm key={`prevoz:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="obrad" className="p-3">{activeTab === "obrad" ? <ObradForm key={`obrad:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="sluzby" className="p-3">{activeTab === "sluzby" ? <VyberSluzebFormV2 key={`sluzby:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="kvetiny" className="p-3">{activeTab === "kvetiny" ? <VyberKvetinForm key={`kvetiny:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="rec" className="p-3">{activeTab === "rec" ? <RecForm /> : null}</TabsContent>
            <TabsContent value="dokumenty" className="p-3">{activeTab === "dokumenty" ? <DokumentyForm key={`dokumenty:${orderKey}`} /> : null}</TabsContent>
            <TabsContent value="final" className="p-3">{activeTab === "final" ? <FinalniObjednavka key={`final:${orderKey}`} /> : null}</TabsContent>

            {/* === WORKFLOW: Uložit a pokračovat (zakázka) === */}
            {(openGroup === "zakazka" && isZakazkaTab(activeTab) && activeTab !== "zaklad" && activeTab !== "final") && (
              <div style={{ position: "sticky", bottom: 0, zIndex: 50, marginTop: 12 }}>
                <div style={{ display: "flex", gap: 10, padding: 10, borderTop: "1px solid rgba(0,0,0,0.08)", background: "rgba(255,255,255,0.96)", backdropFilter: "blur(6px)" }}>
                    <button
                      type="button"
                      onClick={async () => {
                        const wf = wfLoad(effectiveOrderId);
                        const done = (wf && wf.done) ? wf.done : {};
                        const nowDone = { ...done, [activeTab]: true };
                        const nextTab = zakazkaNext(activeTab);

                        // 1) nejdřív bezpečně uložit zakázku
                        try {
                          if (typeof saveOrder === "function") await saveOrder();
                        } catch (e) {
                          console.warn("workflow saveOrder failed:", e);
                          alert(`Uložení selhalo: ${e?.message || "neznámá chyba"}`);
                          return;
                        }

                        // 2) teprve po úspěchu uložit workflow
                        wfSave(effectiveOrderId, { done: nowDone, lastTab: nextTab });

                        // 3) přepnout na další tab
                        setActiveTab(nextTab);
                      }}
                      style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.12)", fontWeight: 700 }}
                    >
                      Uložit a pokračovat
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        const wf = wfLoad(effectiveOrderId);
                        const done = (wf && wf.done) ? wf.done : {};
                        wfSave(effectiveOrderId, { done, lastTab: activeTab });

                        try {
                          if (typeof saveOrder === "function") await saveOrder();
                          alert("Uloženo.");
                        } catch (e) {
                          alert(`Uložení selhalo: ${e?.message || "neznámá chyba"}`);
                        }
                      }}
                      style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.12)" }}
                    >
                      Uložit
                    </button>

                    <div style={{ flex: 1 }} />

                  <button
                    type="button"
                    onClick={() => {
                      const prev = zakazkaPrev(activeTab);
                      const wf = wfLoad(effectiveOrderId);
                      const done = (wf && wf.done) ? wf.done : {};
                      wfSave(effectiveOrderId, { done, lastTab: prev });
                      setActiveTab(prev);
                    }}
                    style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.12)" }}
                  >
                    Zpět
                  </button>
                </div>
              </div>
            )}              {false && (
                <TabsContent value="kniha" className="p-3">
                  <KnihaZemrelych key={`kniha:${effectiveOrderId||'new'}`} onOpenZakazka={handleOpenZakazka} />
                </TabsContent>
              )}

            <TabsContent value="foto" className="p-3"><FotoDokumentace /></TabsContent>

            <TabsContent value="vyjezdy" className="p-3"><VyjezdyPage /></TabsContent>
            <TabsContent value="obrad1" className="p-3">
              <KalendarObrady
                onOpenZakazka={handleOpenZakazka}
                onOpenObrad={() => {
                  setAppMode("zakazka");
                  setOpenGroup("zakazka");
                  setActiveTab("obrad");
                }}
              />
            </TabsContent>

            <TabsContent value="stat" className="p-3"><Statistiky /></TabsContent>
            </Tabs>
        </div>
      )}
    </div>
  );
}
